/* ============================================================
   HERZ DER KARTEN – search-suggest.js
   Live-Suchvorschlaege fuer die Header-Suchfelder: Desktop-Inline-
   Feld UND Mobile-Inline-Feld teilen dieselbe Mechanik (Datenquelle
   /search/suggest.json, Dropdown direkt unter dem Feld, Debounce,
   Sequenz-Token, Outside-Click/Escape schliesst).
   Ersetzt das fruehere Vollbild-Such-Overlay (entfernt 2026-07):
   dessen Desktop-Einstieg war ein Fake-Suchfeld-Button; jetzt ist
   die Desktop-Suche ein echtes Formular (Enter/Lupe -> /search).
   ============================================================ */

// i18n-Bruecke: aus layout/theme.liquid injiziert (window.themeStrings / window.routes).
// Bewusst ZUWEISUNG statt const-Deklaration: alle Theme-Skripte teilen sich den globalen
// Scope, eine zweite `const T` wuerde einen SyntaxError werfen und die Datei stilllegen.
// Die Fallbacks halten die Datei lauffaehig, falls das Layout die Injection nicht liefert.
window.themeStrings = window.themeStrings || {};
window.routes = window.routes || {};


const SearchSuggest = {

  // Beide Helfer liegen seit 2026-09-02 in assets/hdk-util.js (Audit TEST-4) und
  // sind dort ohne Browser getestet: scripts/unit/hdk-util.test.js. Die Methoden
  // bleiben als duenne Weiterleitung stehen, damit alle Aufrufstellen unten
  // unveraendert weiterlaufen — auch "const esc = this.escapeHTML", das die
  // Methode aus ihrer Bindung loest: die Weiterleitung nutzt selbst kein "this".

  // Externe Strings (Nutzereingabe, API-Daten) vor innerHTML-Einsatz escapen —
  // sonst bricht ein Titel mit <, & oder Anfuehrungszeichen das Markup (Self-XSS).
  escapeHTML(value) {
    return window.hdk.escapeHTML(value);
  },

  // /search/suggest.json liefert price bereits in Franken (nicht in Rappen) —
  // daher chfAusFranken und NICHT chfAusRappen wie im Versandfortschritt. Die
  // beiden Formate unterscheiden sich zusaetzlich im Trennzeichen (hier ein
  // normales Leerzeichen, dort ein geschuetztes); hdk-util.js begruendet, warum
  // sie getrennt bleiben.
  formatMoney(amount) {
    return window.hdk.chfAusFranken(amount);
  },

  async fetchProducts(query) {
    const res  = await fetch(`${window.routes.predictiveSearch}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`);
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const data = await res.json();
    return data.resources?.results?.products || [];
  },

  resultsHTML(products, query) {
    const esc = this.escapeHTML;
    const q = esc(query);
    const allUrl = `${window.routes.search}?type=product&q=${encodeURIComponent(query)}`;

    if (products.length === 0) {
      return `
        <div class="search-suggest__section">
          <p class="search-suggest__empty">${window.themeStrings.searchNoResults.replace('__QUERY__', q)}</p>
          <a href="${allUrl}" class="search-suggest__all-link">${window.themeStrings.searchAllResults}</a>
        </div>`;
    }

    const items = products.map(p => `
      <a href="${esc(p.url)}" class="search-product-card">
        <div class="search-product-card__img-wrap">
          ${p.featured_image?.url
            ? `<img src="${esc(p.featured_image.url)}" alt="${esc(p.title)}" loading="lazy" width="80" height="80">`
            : ''}
        </div>
        <div class="search-product-card__info">
          <p class="search-product-card__vendor">${esc(p.vendor || '')}</p>
          <p class="search-product-card__title">${esc(p.title)}</p>
          <p class="search-product-card__price">${this.formatMoney(p.price)}</p>
        </div>
      </a>
    `).join('');

    return `
      <div class="search-suggest__section">
        <p class="search-suggest__section-label">${window.themeStrings.searchProductsLabel}</p>
        <div class="search-suggest__products">${items}</div>
        <a href="${allUrl}" class="search-suggest__all-link">${window.themeStrings.searchAllResultsFor.replace('__QUERY__', q)}</a>
      </div>`;
  },

  // Ein Feld + sein Dropdown verdrahten; jede Instanz mit eigenem State (Debounce,
  // Sequenz-Token: eine spaeter eintreffende Antwort einer aelteren Query darf die
  // aktuelle nicht ueberschreiben — Debounce verhindert nur geplante, nicht laufende Fetches).
  bind({ input, results, container, status }) {
    if (!input || !results || !container) return;
    let timeout = null;
    let seq = 0;

    // Unsichtbare aria-live-Zeile (A11Y-12): das Dropdown erschien fuer
    // Screenreader stumm. Texte kommen als data-Attribute aus dem Markup
    // (diese Datei ist ein statisches Asset ohne Liquid). Leer = keine Ansage.
    const sagAn = (text) => { if (status) status.textContent = text || ''; };
    const trefferText = (n) => {
      if (!status) return '';
      if (n === 0) return status.dataset.tplNone || '';
      if (n === 1) return status.dataset.tplCountOne || '';
      return (status.dataset.tplCount || '__N__').replace('__N__', n);
    };

    // Kein aria-expanded am Eingabefeld (Audit 2026-10-02, A11Y-6): auf
    // type=search ohne role=combobox ist es ungueltiges ARIA. Den Zustand
    // tragen aria-hidden am Panel und die Live-Region (sagAn).
    const open = () => {
      results.classList.add('is-open');
      results.setAttribute('aria-hidden', 'false');
    };
    const close = () => {
      results.classList.remove('is-open');
      results.setAttribute('aria-hidden', 'true');
      sagAn('');
    };

    const run = async (query) => {
      const s = ++seq;
      try {
        const products = await this.fetchProducts(query);
        if (s !== seq) return;
        results.innerHTML = this.resultsHTML(products, query);
        open();
        sagAn(trefferText(products.length));
      } catch (err) {
        console.error('Search error:', err);
      }
    };

    input.addEventListener('input', () => {
      const query = input.value.trim();
      clearTimeout(timeout);
      if (query.length < 2) {
        close();
        return;
      }
      timeout = setTimeout(() => run(query), 300);
    });

    // Bereits geladene Vorschlaege bei erneutem Fokus wieder zeigen
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= 2 && results.innerHTML.trim() !== '') open();
    });

    document.addEventListener('click', (e) => {
      if (!container.contains(e.target)) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });
  },

  init() {
    this.bind({
      input: document.getElementById('header-search-input'),
      results: document.getElementById('header-search-suggestions'),
      container: document.getElementById('header-search'),
      status: document.getElementById('header-search-status')
    });
    this.bind({
      input: document.getElementById('mobile-search-input'),
      results: document.getElementById('mobile-search-suggestions'),
      container: document.querySelector('.header__mobile-search'),
      status: document.getElementById('mobile-search-status')
    });
  }

};

document.addEventListener('DOMContentLoaded', () => {
  SearchSuggest.init();
});
